/**
 * AiCreditsUpsell — turns the Sense AI credit hard-stop into a top-up moment.
 *
 * Listens for the `ai-credits-exhausted` window event (dispatched by the
 * axios 402 interceptor in lib/api.js when the backend blocks an AI action).
 * Org Admins / Super Admins get a toast with an "Add credits" action that
 * opens the existing AddCreditsDialog inline; other roles get a "contact
 * your admin" nudge. De-duped to one toast per 8s.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { AddCreditsDialog } from "@/components/billing/AddCreditsDialog";

const CAN_TOPUP = new Set(["super_admin", "org_admin"]);

export default function AiCreditsUpsell() {
    const { user } = useAuth() || {};
    const [showAddCredits, setShowAddCredits] = useState(false);
    const lastSeen = useRef(0);

    useEffect(() => {
        const handler = (e) => {
            const p = e.detail || {};
            const now = Date.now();
            if (now - lastSeen.current < 8000) return;
            lastSeen.current = now;

            const canTopup = CAN_TOPUP.has(user?.role);
            const usedInc = (p.included != null)
                ? `Used ${Number(p.used || 0).toLocaleString()} / ${Number(p.included).toLocaleString()} credits.`
                : "";

            toast("Sense AI credits used up", {
                description: `${p.message || "AI features are paused for this billing period."} ${usedInc}`.trim(),
                icon: <Sparkles className="h-4 w-4" />,
                duration: 10000,
                id: "ai-credits-exhausted",
                className: "border-amber-500/40",
                action: canTopup
                    ? { label: "Add credits", onClick: () => setShowAddCredits(true) }
                    : undefined,
            });
        };
        window.addEventListener("ai-credits-exhausted", handler);
        return () => window.removeEventListener("ai-credits-exhausted", handler);
    }, [user]);

    return <AddCreditsDialog open={showAddCredits} onOpenChange={setShowAddCredits} />;
}
